import { useState, useCallback, useMemo, useEffect } from 'react';
import { formatCellValue } from '../lib/utils';
import { employeesAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Upload,
  FileSpreadsheet,
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertCircle,
  Download,
  RefreshCw,
  Users,
  ArrowRight,
  CheckCircle2,
  XCircle,
  HelpCircle,
} from 'lucide-react';

const CONTENT_TEAM_ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer' },
  { value: 'analyst', label: 'Analyst' },
  { value: 'approver', label: 'Approver' },
  { value: 'admin', label: 'Admin' },
];
const CONTENT_TEAM_ROLE_VALUES = CONTENT_TEAM_ROLE_OPTIONS.map(role => role.value);

// Employee fields that can be mapped from CSV
const EMPLOYEE_FIELDS = [
  { key: 'email', label: 'Email', required: true, description: 'Employee email address' },
  { key: 'first_name', label: 'First Name', required: true, description: 'Employee first name' },
  { key: 'last_name', label: 'Last Name', required: true, description: 'Employee last name' },
  { key: 'job_title', label: 'Job Title', required: false, description: 'Position or role' },
  { key: 'department', label: 'Department', required: false, description: 'Department name' },
  { key: 'country', label: 'Country', required: false, description: 'Country of residence' },
  { key: 'region', label: 'Region', required: false, description: 'Region or state' },
  { key: 'content_team_role', label: 'Content Team Role', required: false, description: 'Optional: viewer, analyst, approver, or admin' },
];

// Sample CSV template. Rows cover every content-team role option (blank, analyst,
// viewer, admin, approver) and the known regions (NA, EMEA, APAC, LATAM).
const SAMPLE_CSV = `email,first_name,last_name,job_title,department,country,region,content_team_role
john.doe@company.com,John,Doe,Software Engineer,Engineering,United States,NA,
jane.smith@company.com,Jane,Smith,HR Manager,Human Resources,United Kingdom,EMEA,analyst
priya.patel@company.com,Priya,Patel,Data Analyst,Operations,Singapore,APAC,viewer
mateus.silva@company.com,Mateus,Silva,Marketing Lead,Marketing,Brazil,LATAM,admin
bob.wilson@company.com,Bob,Wilson,Sales Rep,Sales,Canada,NA,approver`;

export function BulkImportDialog({ isOpen, onClose, onSuccess }) {
  // Wizard steps: 1=Upload, 2=Map, 3=Preview, 4=Import
  const [step, setStep] = useState(1);
  const [, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState({ headers: [], rows: [] });
  const [droppedRowCount, setDroppedRowCount] = useState(0);
  const [columnMapping, setColumnMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [bulkAddToContentTeam, setBulkAddToContentTeam] = useState(false);
  const [bulkContentTeamRole, setBulkContentTeamRole] = useState('viewer');
  // Server-authoritative validation (EIP-296): the Preview step calls a backend
  // dry-run so duplicates, cross-tenant conflicts and seat limits are shown to
  // the admin BEFORE any user is created.
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validateError, setValidateError] = useState(null);

  // Parse CSV file
  const parseCSV = useCallback((text) => {
    try {
      const lines = text.split(/\r?\n/).filter(line => line.trim());
      if (lines.length < 2) {
        throw new Error('CSV must have at least a header row and one data row');
      }

      // Parse header
      const headers = parseCSVLine(lines[0]);
      
      // Parse data rows. A row whose column count doesn't match the header is
      // malformed (stray comma, unquoted value); count how many we drop so the
      // admin is told rather than silently losing rows (EIP-296).
      const rows = [];
      let dropped = 0;
      for (let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length === headers.length) {
          const row = {};
          headers.forEach((header, idx) => {
            row[header] = values[idx];
          });
          rows.push(row);
        } else {
          dropped++;
        }
      }

      if (rows.length === 0) {
        throw new Error('No valid data rows found in CSV');
      }

      setCsvData({ headers, rows });
      setDroppedRowCount(dropped);
      setParseError(null);
      
      // Auto-map columns based on header names
      const autoMapping = {};
      headers.forEach(header => {
        const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '_');
        const matchedField = EMPLOYEE_FIELDS.find(f => {
          const normalizedKey = f.key.toLowerCase();
          const normalizedLabel = f.label.toLowerCase().replace(/[^a-z0-9]/g, '_');
          return normalizedHeader === normalizedKey || 
                 normalizedHeader === normalizedLabel ||
                 normalizedHeader.includes(normalizedKey) ||
                 normalizedKey.includes(normalizedHeader);
        });
        if (matchedField) {
          autoMapping[matchedField.key] = header;
        }
      });
      setColumnMapping(autoMapping);
      setStep(2);
    } catch (error) {
      setParseError(error.message);
      toast.error('Parse Error', { description: error.message });
    }
  }, []);

  // Parse a single CSV line (handles quoted values)
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  // Handle file upload
  const handleFileUpload = useCallback((file) => {
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Invalid File', { description: 'Please upload a CSV file' });
      return;
    }

    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      parseCSV(e.target.result);
    };
    reader.onerror = () => {
      toast.error('Read Error', { description: 'Failed to read the file' });
    };
    reader.readAsText(file);
  }, [parseCSV]);

  // Drag and drop handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  }, [handleFileUpload]);

  // Download sample CSV
  const downloadSample = useCallback(() => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'employees_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  }, []);

  // Validate mapping - check required fields
  const mappingValidation = useMemo(() => {
    const errors = [];
    const warnings = [];
    
    EMPLOYEE_FIELDS.forEach(field => {
      if (field.required && !columnMapping[field.key]) {
        errors.push(`${field.label} is required but not mapped`);
      }
    });

    // Check for duplicate mappings
    const mappedColumns = Object.values(columnMapping);
    const duplicates = mappedColumns.filter((col, idx) => 
      col && mappedColumns.indexOf(col) !== idx
    );
    if (duplicates.length > 0) {
      errors.push(`Column "${duplicates[0]}" is mapped to multiple fields`);
    }

    return { errors, warnings, isValid: errors.length === 0 };
  }, [columnMapping]);

  // Columns present in the uploaded file that aren't mapped to any supported
  // field. These are dropped on import — surface them so the admin isn't left
  // wondering where their extra columns went (EIP-295). Recomputes as the user
  // maps columns, so mapping a leftover header removes it from the list.
  const ignoredColumns = useMemo(() => {
    const mappedSources = new Set(Object.values(columnMapping).filter(Boolean));
    return csvData.headers.filter(header => !mappedSources.has(header));
  }, [csvData.headers, columnMapping]);

  // Transform rows based on mapping
  const transformedRows = useMemo(() => {
    return csvData.rows.map((row, idx) => {
      const transformed = { _rowIndex: idx + 2 }; // +2 for 1-based index + header row
      const errors = [];

      EMPLOYEE_FIELDS.forEach(field => {
        const csvColumn = columnMapping[field.key];
        if (csvColumn) {
          transformed[field.key] = row[csvColumn]?.trim() || '';
        } else {
          transformed[field.key] = '';
        }

        // Validate required fields
        if (field.required && !transformed[field.key]) {
          errors.push(`${field.label} is missing`);
        }
      });

      // Validate email format
      if (transformed.email && !transformed.email.includes('@')) {
        errors.push('Invalid email format');
      }
      if (transformed.content_team_role) {
        transformed.content_team_role = transformed.content_team_role.toLowerCase();
        if (!CONTENT_TEAM_ROLE_VALUES.includes(transformed.content_team_role)) {
          errors.push('Content Team Role must be viewer, analyst, approver, or admin');
        }
      }
      transformed.add_to_content_team = bulkAddToContentTeam || Boolean(transformed.content_team_role);
      if (bulkAddToContentTeam && !transformed.content_team_role) {
        transformed.content_team_role = bulkContentTeamRole;
      }

      transformed._errors = errors;
      transformed._isValid = errors.length === 0;
      return transformed;
    });
  }, [bulkAddToContentTeam, bulkContentTeamRole, csvData.rows, columnMapping]);

  // Build the API payload from the mapped rows. Sends EVERY row (not just the
  // client-valid ones) so the server is the single source of truth and its
  // per-row results line up with these rows by array index.
  const buildPayload = useCallback((rows) => rows.map(row => ({
    email: row.email,
    first_name: row.first_name,
    last_name: row.last_name,
    job_title: row.job_title || null,
    department: row.department || null,
    country: row.country || null,
    region: row.region || null,
    send_invitation: true, // Send invitations by default for bulk import
    add_to_content_team: Boolean(row.add_to_content_team),
    content_team_role: row.add_to_content_team ? row.content_team_role || bulkContentTeamRole : null,
  })), [bulkContentTeamRole]);

  // Validate against the backend whenever the Preview step is shown or its rows
  // change (e.g. the "add all to content team" toggle). This is the authoritative
  // pre-commit check: duplicates, cross-tenant conflicts and seat capacity.
  useEffect(() => {
    if (step !== 3) return undefined;
    let cancelled = false;
    (async () => {
      setValidating(true);
      setValidateError(null);
      try {
        const result = await employeesAPI.validateBulkImport(buildPayload(transformedRows));
        if (!cancelled) setValidation(result);
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        const message = (detail && typeof detail === 'object' ? detail.message : detail)
          || err.message || 'Could not validate the import';
        setValidation(null);
        setValidateError(message);
        toast.error('Validation Failed', { description: message });
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step, transformedRows, buildPayload]);

  // Perform bulk import (only reachable once validation says it can import).
  const handleImport = async () => {
    if (!validation || !validation.can_import) return;

    setImporting(true);
    try {
      const result = await employeesAPI.bulkImport(buildPayload(transformedRows));
      setImportResults(result);
      setStep(4);
      toast.success('Import Complete', { description: `Successfully imported ${result.success_count} employees` });
      if (onSuccess) onSuccess();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const description = (detail && typeof detail === 'object' ? detail.message : detail)
        || error.message || 'Failed to import employees';
      toast.error('Import Failed', { description });
      // Capacity race (409) between validate and import — refresh the banner.
      if (error.response?.status === 409) {
        try {
          const refreshed = await employeesAPI.validateBulkImport(buildPayload(transformedRows));
          setValidation(refreshed);
        } catch (_) { /* keep the toast; the banner stays as-is */ }
      }
    } finally {
      setImporting(false);
    }
  };

  // Reset wizard
  const handleReset = () => {
    setStep(1);
    setCsvFile(null);
    setCsvData({ headers: [], rows: [] });
    setDroppedRowCount(0);
    setColumnMapping({});
    setImportResults(null);
    setParseError(null);
    setBulkAddToContentTeam(false);
    setBulkContentTeamRole('viewer');
    setValidation(null);
    setValidateError(null);
    setValidating(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <FileSpreadsheet className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Bulk Import Employees
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Import employees from a CSV file
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {['Upload CSV', 'Map Columns', 'Preview', 'Complete'].map((label, idx) => {
              const stepNum = idx + 1;
              const isActive = step === stepNum;
              const isComplete = step > stepNum;
              return (
                <div key={label} className="flex items-center">
                  <div className={`flex items-center gap-2 ${
                    isActive ? 'text-purple-600' : isComplete ? 'text-green-600' : 'text-gray-400'
                  }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      isActive 
                        ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-600' 
                        : isComplete 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-600' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                    }`}>
                      {isComplete ? <Check className="w-4 h-4" /> : stepNum}
                    </div>
                    <span className="hidden sm:inline text-sm font-medium">{label}</span>
                  </div>
                  {idx < 3 && (
                    <ChevronRight className="w-5 h-5 mx-2 text-gray-300 dark:text-gray-600" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  dragActive 
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                    : 'border-gray-300 dark:border-gray-600 hover:border-purple-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Drag and drop your CSV file here
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  or click to browse
                </p>
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => handleFileUpload(e.target.files[0])}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg cursor-pointer transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Choose File
                </label>
              </div>

              {parseError && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                    <AlertCircle className="w-5 h-5" />
                    <span>{parseError}</span>
                  </div>
                </div>
              )}

              {/* Template Download */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-blue-800 dark:text-blue-300 font-medium mb-2">
                      Need a template?
                    </p>
                    <p className="text-sm text-blue-700 dark:text-blue-400 mb-3">
                      Download our sample CSV template with the correct column format.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadSample}
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Template
                    </Button>
                  </div>
                </div>
              </div>

              {/* Field Reference */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Available Fields
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  These are the only fields that can be imported — any extra columns in your file are ignored.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {EMPLOYEE_FIELDS.map(field => (
                    <div
                      key={field.key}
                      className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {field.label}
                      </span>
                      {field.required && (
                        <Badge variant="secondary" className="text-xs bg-red-100 text-red-700">
                          Required
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Map Columns */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Map CSV Columns to Employee Fields
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Match your CSV columns to the employee attributes
                  </p>
                </div>
                <Badge variant="outline" className="text-sm">
                  {csvData.headers.length} columns found
                </Badge>
              </div>

              {/* Mapping Grid */}
              <div className="space-y-3">
                {EMPLOYEE_FIELDS.map(field => (
                  <div
                    key={field.key}
                    className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {field.label}
                        </span>
                        {field.required && (
                          <Badge variant="secondary" className="text-xs bg-red-100 text-red-700">
                            Required
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {field.description}
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400" />
                    <div className="flex-1">
                      <select
                        value={columnMapping[field.key] || ''}
                        onChange={(e) => setColumnMapping({
                          ...columnMapping,
                          [field.key]: e.target.value || undefined,
                        })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">-- Select column --</option>
                        {csvData.headers.map(header => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              {/* Validation Messages */}
              {!mappingValidation.isValid && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-700 dark:text-red-400">
                        Please fix the following issues:
                      </p>
                      <ul className="mt-1 text-sm text-red-600 dark:text-red-400 list-disc list-inside">
                        {mappingValidation.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Ignored columns notice — extra CSV columns are dropped on import (EIP-295) */}
              {ignoredColumns.length > 0 && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-700 dark:text-amber-400">
                        {ignoredColumns.length} column{ignoredColumns.length > 1 ? 's' : ''} in your file won't be imported
                      </p>
                      <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                        Only the fields listed above are supported. If one of these should populate a field above, pick it in that field's dropdown — otherwise it will be ignored.
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {ignoredColumns.map(col => (
                          <Badge
                            key={col}
                            variant="secondary"
                            className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                          >
                            {col}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Preview (server-validated, EIP-296) */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Add-all-to-content-team toggle — changing it re-validates */}
              <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={bulkAddToContentTeam}
                    onChange={(e) => setBulkAddToContentTeam(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded text-purple-600 focus:ring-purple-600"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-gray-900 dark:text-white">
                      Add all imported employees to Content Team
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      Content team members use the shared seat pool, the same as employees. A mapped Content Team Role column overrides the default role per row.
                    </span>
                  </span>
                </label>
                {bulkAddToContentTeam && (
                  <div className="mt-3">
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Default content team role
                    </label>
                    <select
                      value={bulkContentTeamRole}
                      onChange={(e) => setBulkContentTeamRole(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      {CONTENT_TEAM_ROLE_OPTIONS.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Initial validation spinner */}
              {validating && !validation && (
                <div className="flex items-center justify-center gap-3 py-10 text-gray-500 dark:text-gray-400">
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Validating with server…</span>
                </div>
              )}

              {/* Validation could not run */}
              {validateError && !validating && !validation && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-start gap-2 text-red-700 dark:text-red-400">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Couldn't validate the import</p>
                      <p className="mt-1 text-sm">{validateError}</p>
                    </div>
                  </div>
                </div>
              )}

              {validation && (
                <>
                  {/* Blocking banners — the import is disabled until these clear */}
                  {validation.subscription_blocked && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <div className="flex items-start gap-2 text-red-700 dark:text-red-400">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{validation.subscription_block_message}</span>
                      </div>
                    </div>
                  )}
                  {validation.would_exceed_seats && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <div className="flex items-start gap-2 text-red-700 dark:text-red-400">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">Not enough seats for this import</p>
                          <p className="mt-1 text-sm">
                            Importing {validation.writable_count} employee{validation.writable_count === 1 ? '' : 's'}, but only{' '}
                            {Math.max(0, validation.seats_available)} seat{Math.max(0, validation.seats_available) === 1 ? '' : 's'} available. Remove rows or add seats, then try again.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {validation.would_exceed_content_team && !validation.would_exceed_seats && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <div className="flex items-start gap-2 text-red-700 dark:text-red-400">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium">Not enough Content Team seats</p>
                          <p className="mt-1 text-sm">
                            Adding {validation.content_team_required} Content Team member{validation.content_team_required === 1 ? '' : 's'}, but only{' '}
                            {Math.max(0, validation.content_team_available)} Content Team seat{Math.max(0, validation.content_team_available) === 1 ? '' : 's'} available.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Stats (authoritative counts from the server) */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-gray-900 dark:text-white">{validation.total}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Rows</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-green-600">{validation.writable_count}</p>
                      <p className="text-sm text-green-700 dark:text-green-400">Ready to Import</p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-red-600">{validation.invalid_count}</p>
                      <p className="text-sm text-red-700 dark:text-red-400">Has Errors</p>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-purple-600">{validation.content_team_count}</p>
                      <p className="text-sm text-purple-700 dark:text-purple-400">Content Team</p>
                    </div>
                  </div>

                  {validation.revive_count > 0 && (
                    <div className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-400">
                      <RefreshCw className="w-4 h-4" />
                      <span>
                        {validation.revive_count} previously removed employee{validation.revive_count === 1 ? '' : 's'} will be re-activated.
                      </span>
                    </div>
                  )}

                  {validating && (
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <RefreshCw className="w-3 h-3 animate-spin" />
                      <span>Re-validating…</span>
                    </div>
                  )}

                  {/* Preview Table */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto max-h-[400px]">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            {EMPLOYEE_FIELDS.filter(f => columnMapping[f.key]).map(field => (
                              <th key={field.key} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                {field.label}
                              </th>
                            ))}
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Content Team</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {transformedRows.slice(0, 100).map((row, idx) => {
                            const server = validation.rows[idx];
                            const status = server?.status || (row._isValid ? 'valid' : 'invalid');
                            const rowErrors = server?.errors || row._errors || [];
                            const rowWarnings = server?.warnings || [];
                            return (
                              <tr key={idx} className={status === 'invalid' ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                                <td className="px-3 py-2 text-gray-500">{row._rowIndex}</td>
                                <td className="px-3 py-2">
                                  {status === 'invalid' ? (
                                    <div className="flex items-center gap-1">
                                      <XCircle className="w-4 h-4 text-red-500" />
                                      <span className="text-xs text-red-600" title={rowErrors.join(', ')}>
                                        {rowErrors.length} error{rowErrors.length === 1 ? '' : 's'}
                                      </span>
                                    </div>
                                  ) : status === 'will_revive' ? (
                                    <div className="flex items-center gap-1" title="Previously removed — will be re-activated">
                                      <RefreshCw className="w-4 h-4 text-blue-500" />
                                      <span className="text-xs text-blue-600">Will revive</span>
                                    </div>
                                  ) : rowWarnings.length > 0 ? (
                                    <div className="flex items-center gap-1" title={rowWarnings.join(', ')}>
                                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                                      <AlertCircle className="w-4 h-4 text-amber-500" />
                                    </div>
                                  ) : (
                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                  )}
                                </td>
                                {EMPLOYEE_FIELDS.filter(f => columnMapping[f.key]).map(field => (
                                  <td
                                    key={field.key}
                                    className="px-3 py-2 text-gray-900 dark:text-white truncate max-w-[150px]"
                                    title={row[field.key]}
                                  >
                                    {formatCellValue(row[field.key])}
                                  </td>
                                ))}
                                <td className="px-3 py-2 text-gray-900 dark:text-white">
                                  {row.add_to_content_team ? (
                                    <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                                      {CONTENT_TEAM_ROLE_OPTIONS.find(role => role.value === row.content_team_role)?.label || 'Viewer'}
                                    </Badge>
                                  ) : (
                                    <span className="text-gray-400">No</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {transformedRows.length > 100 && (
                      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 text-sm text-gray-500 text-center">
                        Showing first 100 of {transformedRows.length} rows
                      </div>
                    )}
                  </div>

                  {validation.invalid_count > 0 && (
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                        <AlertCircle className="w-5 h-5" />
                        <span>
                          {validation.invalid_count} row{validation.invalid_count === 1 ? '' : 's'} with errors will be skipped during import.
                        </span>
                      </div>
                    </div>
                  )}

                  {droppedRowCount > 0 && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">
                          {droppedRowCount} row{droppedRowCount === 1 ? '' : 's'} in your file {droppedRowCount === 1 ? 'was' : 'were'} skipped while reading because {droppedRowCount === 1 ? 'its' : 'their'} column count didn't match the header.
                        </span>
                      </div>
                    </div>
                  )}

                  {ignoredColumns.length > 0 && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">
                          Ignored column{ignoredColumns.length > 1 ? 's' : ''} (not imported): {ignoredColumns.join(', ')}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 4: Complete */}
          {step === 4 && importResults && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Import Complete!
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                Successfully imported {importResults.success_count} employees
              </p>

              <div className="grid grid-cols-2 gap-4 max-w-md mx-auto mb-6">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4">
                  <p className="text-2xl font-bold text-green-600">
                    {importResults.success_count}
                  </p>
                  <p className="text-sm text-green-700 dark:text-green-400">Imported</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4">
                  <p className="text-2xl font-bold text-red-600">
                    {importResults.error_count || 0}
                  </p>
                  <p className="text-sm text-red-700 dark:text-red-400">Failed</p>
                </div>
              </div>

              {importResults.errors && importResults.errors.length > 0 && (
                <div className="text-left max-w-lg mx-auto mb-6">
                  <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Errors:
                  </h4>
                  <ul className="text-sm text-red-600 dark:text-red-400 space-y-1">
                    {importResults.errors.slice(0, 10).map((err, idx) => (
                      <li key={idx}>• {err}</li>
                    ))}
                    {importResults.errors.length > 10 && (
                      <li>...and {importResults.errors.length - 10} more</li>
                    )}
                  </ul>
                </div>
              )}

              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={handleReset}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Import More
                </Button>
                <Button onClick={onClose}>
                  <Users className="w-4 h-4 mr-2" />
                  View Employees
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step < 4 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div>
              {step > 1 && (
                <Button
                  variant="ghost"
                  onClick={() => setStep(step - 1)}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {step === 2 && (
                <Button
                  onClick={() => setStep(3)}
                  disabled={!mappingValidation.isValid}
                >
                  Preview Import
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              {step === 3 && (
                <Button
                  onClick={handleImport}
                  disabled={importing || validating || !validation || !validation.can_import}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {importing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : validating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import {validation ? validation.writable_count : 0} {(validation ? validation.writable_count : 0) === 1 ? 'Employee' : 'Employees'}
                      {validation && validation.invalid_count > 0 ? ` (${validation.invalid_count} skipped)` : ''}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BulkImportDialog;
